import React from 'react';
import { Avatar } from '../../components/avatar';
import { Button } from '../../components/button';

interface FacilityContactCardProps {
  facilityContact: {
    id: string;
    contact: {
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone_number?: string;
      phone_number_extension?: string;
    };
    role?: string;
  };
  onEdit?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  onDelete?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  roleMap?: Record<string, string>;
  canEdit?: boolean;
}

export const FacilityContactCard: React.FC<FacilityContactCardProps> = ({
  facilityContact,
  onEdit,
  onDelete,
  roleMap = {},
  canEdit = false,
}) => {
  const fullName =
    [facilityContact.contact.first_name, facilityContact.contact.last_name]
      .filter(Boolean)
      .join(' ') || 'Unnamed Contact';
  const initials = [facilityContact.contact.first_name?.[0], facilityContact.contact.last_name?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();

  return (
    <div className="tw:relative tw:flex tw:flex-col tw:md:flex-row tw:items-start tw:md:items-center tw:gap-4 tw:bg-zinc-100/50 tw:p-6 tw:rounded-lg tw:dark:border tw:dark:border-white/10 tw:dark:bg-white/5 tw:dark:shadow-none">
      {/* Avatar */}
      <Avatar
        initials={initials}
        className="tw:!hidden tw:md:!block tw:size-12 tw:mb-4 tw:md:mb-0 tw:text-white tw:bg-brand-green-0"
      />
      {/* Info */}
      <div className="tw:flex-1 tw:flex tw:flex-col tw:gap-1 tw:min-w-0">
        <div className="tw:text-base tw:md:text-lg tw:text-zinc-700 tw:dark:text-white tw:truncate">
          {fullName}
        </div>
        <div className="tw:mt-1 tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-0 tw:md:mt-0">
          {/* Role */}
          <div className="tw:md:w-1/3 tw:md:pr-2 tw:truncate tw:text-xs tw:md:text-sm tw:text-zinc-400 tw:dark:text-zinc-300">
            {facilityContact.role ? (
              <span className="tw:truncate">
                {roleMap[facilityContact.role] || facilityContact.role}
              </span>
            ) : (
              <span className="tw:opacity-50">--</span>
            )}
          </div>
          {/* Email */}
          <div className="tw:md:w-1/3 tw:md:px-2 tw:truncate tw:text-xs tw:md:text-sm tw:text-zinc-700 tw:dark:text-zinc-200">
            {facilityContact.contact.email ? (
              <a
                href={`mailto:${facilityContact.contact.email}`}
                className="tw:flex tw:items-center tw:truncate tw:!no-underline"
                title={`Email ${facilityContact.contact.email}`}
              >
                <i className="fa-solid fa-envelope tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-300" />
                <span className="tw:truncate">{facilityContact.contact.email}</span>
              </a>
            ) : (
              <span className="tw:opacity-50">--</span>
            )}
          </div>
          {/* Phone */}
          <div className="tw:md:w-1/3 tw:md:pl-2 tw:truncate tw:text-xs tw:md:text-sm tw:text-zinc-700 tw:dark:text-zinc-200">
            {facilityContact.contact.phone_number ? (
              <a
                href={`tel:${facilityContact.contact.phone_number}`}
                className="tw:flex tw:items-center tw:truncate tw:!no-underline"
                title={`Call ${facilityContact.contact.phone_number}`}
              >
                <i className="fa-solid fa-phone tw:mr-2 tw:text-zinc-400 tw:dark:text-zinc-300" />
                <span className="tw:truncate">
                  {facilityContact.contact.phone_number}
                  {facilityContact.contact.phone_number_extension &&
                    ` ext. ${facilityContact.contact.phone_number_extension}`}
                </span>
              </a>
            ) : (
              <span className="tw:opacity-50">--</span>
            )}
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="tw:absolute tw:top-4 tw:right-4 tw:flex tw:gap-2 tw:md:static tw:md:ml-6 tw:md:self-center tw:md:justify-end">
          {onEdit && (
            <Button
              type="button"
              plain={true}
              onClick={() => onEdit(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:dark:text-zinc-300 tw:dark:hover:text-brand-green-1 tw:p-2"
              aria-label="Edit Contact"
            >
              <i className="fa-regular fa-pen-to-square" />
            </Button>
          )}
          {onDelete && (
            <Button
              type="button"
              plain={true}
              onClick={() => onDelete(facilityContact)}
              className="tw:p-2 tw:text-zinc-400 tw:dark:text-zinc-300 tw:hover:text-stoplight-danger tw:dark:hover:!text-stoplight-danger"
              aria-label="Delete Contact"
            >
              <i className="fa-regular fa-trash" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
